import { useState } from 'react';
import type { AppSession, MeView, PatientView } from '../../lib/types';
import type { AuthConfig } from '../../lib/home';
import { goalLabel } from '@heartcoach/care';
import { ApiError, api } from '../../lib/api';
import { fmtClock, fmtCoins, fmtDate, fmtSchedule } from '../../lib/format';
import { displayName } from '../../lib/session';
import { Icon } from '../../components/HeartMark';
import { Avatar, Card, Row } from '../../components/Bits';
import { TreasuryPanel } from '../../components/TreasuryPanel';

export function ProfileTab({ session, me, config, view, onSignOut, reload }: { session: AppSession; me: MeView; config: AuthConfig | null; view: PatientView; onSignOut: () => void; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setOutreach = async (settings: Parameters<typeof api.outreach>[0]) => {
    setBusy(true);
    setError(null);
    try {
      await api.outreach(settings, session.token);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="banner">
        <Avatar name={view.profile.name} size="lg" />
        <div className="banner-main">
          <h1>{view.profile.name}</h1>
          <p className="sub">
            {view.profile.diagnoses.map((d) => d.label).join(' & ') || 'HeartCoach patient'} · member since {fmtDate(view.profile.memberSince)}
          </p>
        </div>
        <div className="trio plain" style={{ minWidth: '280px', margin: 0 }}>
          <div>
            <strong>{view.streak.current}</strong>
            <span>Day streak</span>
          </div>
          <div>
            <strong>{fmtCoins(view.coins.balance)}</strong>
            <span>HeartCoins</span>
          </div>
          <div>
            <strong>{view.coins.level.level}</strong>
            <span>Level</span>
          </div>
        </div>
      </section>

      <div className="grid-2">
        <div className="stack">
          <Card>
            <h2>Your care plan</h2>
            <span className="eyebrow">Medications</span>
            {view.medications.map((m) => (
              <div className="row" key={m.id}>
                <span className="dot-red" aria-hidden="true" />
                <span className="row-main">
                  <span className="row-title">{m.name}</span>
                  <span className="row-sub">
                    {fmtSchedule(m.times)}
                    {m.anticoagulant ? ' · blood thinner' : ''}
                  </span>
                </span>
              </div>
            ))}
            <span className="eyebrow" style={{ marginTop: '1rem', display: 'inline-flex' }}>
              Care goals
            </span>
            {view.goals.map((g, i) => (
              <div className="row" key={i}>
                <span className={`bubble bubble-sm ${g.kind === 'bp' ? 'bubble-red' : g.kind === 'steps' ? 'bubble-green' : 'bubble-purple'}`}>
                  <Icon name={g.kind === 'bp' ? 'target' : g.kind === 'steps' ? 'steps' : 'pill'} size={16} />
                </span>
                <span className="row-main">
                  <span className="row-title">
                    {`${goalLabel(g).title} ${goalLabel(g).target}`}
                  </span>
                </span>
              </div>
            ))}
          </Card>

          <Card className="list">
            <Row icon="profile" tone="green" title="My cardiologist" sub={view.doctor.name} right="›" />
            <Row icon="star" tone="red" title={view.practiceName} sub={me.practice?.orgName ?? 'Your practice'} right="›" />
            <Row icon="bell" tone="gold" title="Messages" sub={view.inboxUnread > 0 ? `${view.inboxUnread} unread` : 'From your care team'} href="#/inbox" right="›" />
            <Row icon="device" tone="blue" title="Connected devices" sub="A cuff, a scale or a watch" href="#/devices" right="›" />
          </Card>

          <Card>
            <div className="row" style={{ paddingTop: 0 }}>
              <span className="bubble bubble-red bubble-sm">
                <Icon name="shield" size={16} />
              </span>
              <span className="row-main">
                <span className="row-title">Powered by CVAUSA</span>
                <span className="row-sub">Cardiovascular Associates of America</span>
              </span>
              <span className="badge badge-green">Verified</span>
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card>
            <h2>Daily check-in</h2>
            <p className="hint">
              {view.outreach.optedOut
                ? 'The coach will not message you first. Your care team can still send you reminders.'
                : `The coach says good morning at ${fmtClock(view.outreach.checkInLocalTime)} your time, and only when it has something to ask.`}
            </p>
            {view.outreach.optedOut ? null : (
              <label className="field">
                <span>Check-in time</span>
                <input type="time" value={view.outreach.checkInLocalTime} disabled={busy} onChange={(e) => void setOutreach({ checkInLocalTime: e.target.value })} />
              </label>
            )}
            {error ? <p className="form-error">{error}</p> : null}
            <button type="button" className="secondary small" disabled={busy} onClick={() => void setOutreach({ optedOut: !view.outreach.optedOut })}>
              {view.outreach.optedOut ? 'Turn check-ins back on' : 'Turn off check-ins'}
            </button>
          </Card>

          <Card>
            <h2>Your identity</h2>
            <p>
              Signed in as <strong>{displayName(session)}</strong>
              {session.via === 'demo' ? <span className="badge"> demo user</span> : session.via === 'dev' ? <span className="badge"> dev session</span> : null}
            </p>
            {session.address ? <p className="hint mono small">{session.address}</p> : <p className="hint">A Smart Agent at your Home, once you sign in there.</p>}
            <button type="button" className="secondary" onClick={onSignOut}>
              Sign out
            </button>
          </Card>
          <TreasuryPanel session={session} config={config} />
        </div>
      </div>
    </>
  );
}
